import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, logAudit } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTime } from "@/context/TimeContext";
import { Search, Sun, Moon, LogOut, ShieldCheck, Lock, Play, Square, Bell, Trash2 } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { toast } from "sonner";
import { fetchAwayMap, awayFor, AwayBadge } from "@/components/AwayBadge";

const ROLE_LABEL = { admin: "Administrator", manager: "Manager", attorney: "Attorney", staff: "Staff" };

const NOTIF_FILTERS = [
  { key: "all", label: "All" },
  { key: "assignment", label: "Assignments" },
  { key: "task", label: "Tasks" },
  { key: "due_soon", label: "Due soon" },
  { key: "overdue_digest", label: "Overdue" },
  { key: "ticket", label: "Ticketing" },
];

const fmtNotifTime = (t) => { try { return new Date(t).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

function NotificationBell() {
  const navigate = useNavigate();
  const [data, setData] = useState({ unread: 0, items: [] });
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const load = useCallback(() => api.get("/notifications").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  const markAll = async () => { try { await api.post("/notifications/mark-read", { all: true }); load(); } catch (e) { console.error("mark all read", e); toast.error("Could not mark notifications read"); } };
  const clearRead = async () => { try { await api.post("/notifications/clear", {}); load(); } catch (e) { console.error("clear read", e); toast.error("Could not clear notifications"); } };
  const openItem = async (n) => {
    try { await api.post("/notifications/mark-read", { id: n.id }); } catch (e) { console.error("mark read", e); }
    setOpen(false);
    if (n.link) navigate(n.link);
    load();
  };
  const shown = filter === "all" ? data.items : data.items.filter((n) => n.type === filter);
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button data-testid="notif-bell" className="relative grid place-items-center h-9 w-9 rounded-sm border border-border hover:bg-accent hover:text-accent-foreground transition-colors">
          <Bell className="h-4 w-4" />
          {data.unread > 0 && <span data-testid="notif-badge" className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 grid place-items-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold leading-none">{data.unread > 9 ? "9+" : data.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-3 py-2 border-b border-border">
          <span className="text-sm font-semibold">Notifications</span>
          <div className="flex items-center gap-3">
            {data.unread > 0 && <button data-testid="notif-mark-all" onClick={markAll} className="text-xs text-primary hover:underline">Mark all read</button>}
            <button data-testid="notif-clear" onClick={clearRead} title="Clear read notifications" className="text-muted-foreground hover:text-foreground"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        </div>
        <div className="flex items-center gap-1 px-2 py-1.5 border-b border-border overflow-x-auto" data-testid="notif-filters">
          {NOTIF_FILTERS.map((f) => (
            <button key={f.key} data-testid={`notif-filter-${f.key}`} onClick={() => setFilter(f.key)}
              className={`text-[11px] px-2 py-1 rounded-sm whitespace-nowrap transition-colors ${filter === f.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"}`}>{f.label}</button>
          ))}
        </div>
        <div className="max-h-96 overflow-y-auto" data-testid="notif-list">
          {shown.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">{filter === "all" ? "You're all caught up." : "Nothing here."}</div>
          ) : shown.map((n) => (
            <button key={n.id} data-testid="notif-item" onClick={() => openItem(n)}
              className={`w-full text-left px-3 py-2.5 border-b border-border/50 last:border-0 hover:bg-accent transition-colors ${!n.read ? "bg-primary/5" : ""}`}>
              <div className="flex items-start gap-2">
                <span className={`h-1.5 w-1.5 rounded-full mt-1.5 shrink-0 ${!n.read ? "bg-primary" : "bg-transparent"}`} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium truncate">{n.title}</span>
                  <span className="block text-xs text-muted-foreground whitespace-pre-line line-clamp-3">{n.body}</span>
                  <span className="block text-[10px] text-muted-foreground mt-0.5">{fmtNotifTime(n.created_at)}</span>
                </span>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const fmtClock = (s) => {
  s = Math.max(0, Math.round(s || 0));
  const h = String(Math.floor(s / 3600)).padStart(2, "0");
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  return `${h}:${m}`;
};

function ClockWidget() {
  const { clockedIn, elapsed, idleNow, clockIn, clockOut } = useTime();
  if (clockedIn) {
    return (
      <div className="hidden sm:flex items-center gap-2" data-testid="clock-widget">
        <span className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm bg-emerald-500/10 text-emerald-700 text-xs font-medium tabular-nums" title={idleNow ? "Idle" : "Active"}>
          <span className={`h-2 w-2 rounded-full ${idleNow ? "bg-amber-400" : "bg-emerald-500"}`} />
          {fmtClock(elapsed)}
        </span>
        <button data-testid="topbar-clock-out" onClick={() => clockOut()}
          className="flex items-center gap-1.5 h-9 px-3 rounded-sm border border-border text-sm hover:bg-accent hover:text-accent-foreground transition-colors">
          <Square className="h-3.5 w-3.5" /> Clock Out
        </button>
      </div>
    );
  }
  return (
    <button data-testid="topbar-clock-in" onClick={() => clockIn && clockIn()}
      className="hidden sm:flex items-center gap-1.5 h-9 px-3 rounded-sm bg-brand-red-grad text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity">
      <Play className="h-3.5 w-3.5" /> Clock In
    </button>
  );
}

export default function Topbar() {
  const { user, logout, dark, setDark } = useAuth();
  const { clockOut } = useTime();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [awayMap, setAwayMap] = useState({});
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (!q.trim() || q.trim().length < 2) { setResults(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search`, { params: { q } });
        if (data.results.some((r) => r.collection === "staff")) setAwayMap(await fetchAwayMap());
        setResults(data.results);
        setOpen(true);
      } catch (e) { console.error("search", e); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const routeFor = (r) => {
    switch (r.collection) {
      case "staff": return "/people";
      case "vendors": return "/vendors";
      case "courts": return `/courts/${r.id}`;
      case "news": return `/news/${r.id}`;
      case "documents": return "/brief-bank";
      default: return "/practice-groups";
    }
  };

  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("");

  return (
    <header className="h-16 shrink-0 border-b border-border bg-card flex items-center gap-4 px-4 lg:px-6" data-testid="topbar">
      <div className="relative flex-1 max-w-xl" ref={boxRef}>
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          data-testid="global-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results && setOpen(true)}
          placeholder="Search people, courts, vendors, briefs, memos…"
          className="w-full h-10 pl-9 pr-3 bg-secondary/60 border border-border rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-primary"
        />
        {open && results && (
          <div className="absolute top-12 left-0 right-0 bg-popover border border-border rounded-sm shadow-xl max-h-96 overflow-y-auto z-50" data-testid="search-results">
            {results.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground">No results for "{q}"</div>
            ) : (
              results.map((r) => (
                <button
                  key={`${r.collection}-${r.id}`}
                  data-testid="search-result-item"
                  onClick={() => { navigate(routeFor(r)); setOpen(false); setQ(""); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-accent hover:text-accent-foreground border-b border-border/50 last:border-0"
                >
                  <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 bg-primary/10 text-primary rounded-sm">{r.type}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium truncate">{r.title}</span>
                    <span className="block text-xs text-muted-foreground truncate">{r.subtitle}</span>
                  </span>
                  {r.collection === "staff" && <AwayBadge away={awayFor(awayMap, r.email)} compact className="shrink-0" />}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      <div className="hidden md:flex items-center gap-1.5 px-2.5 py-1 bg-primary/10 text-primary rounded-sm text-xs font-medium" title="This portal is monitored (DLP)">
        <Lock className="h-3.5 w-3.5" />
        <span>Secured &amp; Monitored</span>
      </div>

      <ClockWidget />

      <NotificationBell />

      <button
        data-testid="theme-toggle"
        onClick={() => setDark(!dark)}
        className="grid place-items-center h-9 w-9 rounded-sm border border-border hover:bg-accent hover:text-accent-foreground transition-colors"
      >
        {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="user-menu-trigger" className="flex items-center gap-2.5 pl-1 pr-2 py-1 rounded-sm hover:bg-accent hover:text-accent-foreground transition-colors">
            <span className="grid place-items-center h-8 w-8 rounded-sm bg-primary text-primary-foreground text-xs font-bold">{initials}</span>
            <span className="hidden sm:block text-left leading-tight">
              <span className="block text-sm font-medium">{user?.name}</span>
              <span className="block text-[11px] text-muted-foreground">{ROLE_LABEL[user?.role]}</span>
            </span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" /> {user?.email}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid="logout-button"
            onClick={async () => { try { await clockOut?.(); } catch (e) { console.warn("clock-out on sign-out failed", e); } logAudit("logout_click", "session"); logout(); }}
            className="text-destructive focus:text-destructive"
          >
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
